/**
 * Métricas de atrasos, faltas e absenteísmo.
 *
 * Privacidade: rankings usam AnonymousId ou strings de segmento. Nenhum CPF
 * ou nome circula neste módulo.
 *
 * v0 AFD-only: sem AEJ, as únicas métricas de ausência derivam de inferência
 * por heurística de "dia de trabalho da empresa".
 */

import type {
  CanonicalDataset,
  DailyJourney,
  DiagnosticAssumptions,
} from '../domain/canonical.types';
import type {
  AbsenceMetrics,
  LeaveBlock,
  LeaveBlocksSummary,
  OccurrenceMetric,
  NumberMetric,
  MinuteMetric,
} from './metrics.types';
import { detectShortJourney } from './journeys';
import { weekdayOf } from '../domain/dates';

/**
 * Limite mínimo (inclusivo) de dias-de-trabalho consecutivos sem ponto pra
 * uma sequência ser considerada "bloco de afastamento" (atestado/licença/
 * férias) em vez de faltas regulares.
 *
 * 5 dias = aproximadamente 1 semana útil; absenteísmo aleatório raramente
 * cobre uma semana inteira contígua, então é um cutoff conservador. Se um
 * colaborador some por 5+ dias úteis seguidos dentro da janela de presença
 * e depois retorna, é muito mais provável que tenha entrado em afastamento
 * legítimo do que que tenha "matado serviço" durante todos esses dias.
 */
export const LEAVE_BLOCK_MIN_DAYS = 5;

// =========================================================================
// Helpers internos
// =========================================================================

/** Conta ocorrências únicas de employeeId em um array de strings. */
function countUniqueEmployees(employeeIds: string[]): number {
  return new Set(employeeIds).size;
}

// =========================================================================
// Funções exportadas
// =========================================================================

/**
 * Conta jornadas que terminaram mais curtas que a escala prevista (déficit
 * superior à tolerância, com pelo menos uma marcação válida).
 *
 * Substitui as antigas `calculateLateArrivals` e `calculateEarlyLeaves`,
 * que aplicavam o mesmo critério de déficit e portanto duplicavam a
 * contagem. Sem horários de início/fim da escala não dá para distinguir
 * "entrada tardia" de "saída antecipada", então o indicador correto é a
 * existência de déficit — apurada uma única vez aqui.
 */
export function calculateShortJourneys(
  journeys: DailyJourney[],
  toleranceMinutes: number,
): OccurrenceMetric {
  const affectedIds: string[] = [];

  for (const journey of journeys) {
    const delta = detectShortJourney(journey, toleranceMinutes);
    if (delta !== null) {
      affectedIds.push(journey.employeeId);
    }
  }

  return {
    kind: 'occurrence',
    code: 'absent.short_journeys',
    label: 'Jornadas mais curtas que a escala',
    helper: `Jornadas em que o trabalhado ficou ${toleranceMinutes} minuto(s) ou mais abaixo do previsto, com pelo menos uma marcação registrada.`,
    assumptionsUsed: ['toleranceMinutes'],
    estimated: false,
    value: affectedIds.length,
    affectedEmployees: countUniqueEmployees(affectedIds),
  };
}

/**
 * Soma o total de minutos de déficit (jornada mais curta) acumulado no
 * período. Permite exibir o impacto em horas, não só em ocorrências.
 */
export function calculateShortJourneyMinutes(
  journeys: DailyJourney[],
  toleranceMinutes: number,
): MinuteMetric {
  let total = 0;
  for (const journey of journeys) {
    const delta = detectShortJourney(journey, toleranceMinutes);
    if (delta !== null) {
      total += delta.minutes;
    }
  }

  return {
    kind: 'minutes',
    code: 'absent.short_journey_minutes',
    label: 'Total de horas de jornada mais curta',
    helper: 'Soma dos minutos de déficit acumulado em jornadas que terminaram abaixo do previsto. Ignora dias com marcações ímpares ou sem marcação.',
    assumptionsUsed: ['toleranceMinutes'],
    estimated: false,
    value: total,
  };
}

// =========================================================================
// Métricas normalizadas por colaborador/mês
// =========================================================================

/**
 * Calcula a quantidade de colaboradores que aparecem em pelo menos 1 jornada.
 */
function countActiveEmployees(journeys: DailyJourney[]): number {
  return new Set(journeys.map(j => j.employeeId)).size;
}

/**
 * Fórmula de normalização: total / activeEmployees × (30 / periodDays).
 * Retorna 0 quando activeEmployees === 0 ou periodDays === 0.
 */
function normalizePerEmployeeMonth(
  total: number,
  activeEmployees: number,
  periodDays: number,
): number {
  if (activeEmployees === 0 || periodDays === 0) return 0;
  return (total / activeEmployees) * (30 / periodDays);
}

/**
 * Calcula o número de dias do período.
 */
function calcPeriodDays(period: { start: string; end: string }): number {
  const start = new Date(period.start);
  const end = new Date(period.end);
  const diffMs = end.getTime() - start.getTime();
  const days = Math.round(diffMs / (1000 * 60 * 60 * 24)) + 1;
  return Math.max(days, 1);
}

export function calcShortJourneysPerEmployeeMonth(
  shortJourneysTotal: number,
  activeEmployees: number,
  periodDays: number,
): NumberMetric {
  const value = normalizePerEmployeeMonth(shortJourneysTotal, activeEmployees, periodDays);
  return {
    kind: 'number',
    code: 'absent.short_journeys_per_employee_month',
    label: 'Jornadas mais curtas / colaborador / mês',
    helper:
      'Total de jornadas mais curtas que a escala, normalizado por colaborador ativo e período de 30 dias. ' +
      'Fórmula: total ÷ colaboradores_ativos × (30 ÷ dias_do_período).',
    estimated: true,
    value,
  };
}

export function calcFaltasPerEmployeeMonth(
  faltasTotal: number,
  activeEmployees: number,
  periodDays: number,
): NumberMetric {
  const value = normalizePerEmployeeMonth(faltasTotal, activeEmployees, periodDays);
  return {
    kind: 'number',
    code: 'absent.faltas_per_employee_month',
    label: 'Faltas / colaborador / mês',
    helper:
      'Total de faltas inferidas normalizado por colaborador ativo e período de 30 dias. ' +
      'Fórmula: total ÷ colaboradores_ativos × (30 ÷ dias_do_período).',
    estimated: true,
    value,
  };
}

// =========================================================================
// Faltas inferidas (heurística "dia de trabalho da empresa")
// =========================================================================

/**
 * Threshold de presença mínima para considerar um dia como "dia de trabalho da empresa"
 * na heurística de faltas inferidas.
 *
 * - Dias úteis (seg–sex): 30% é suficiente. Empresas com workforce rotativo
 *   (plantão alternado, escalas 6×1, férias escalonadas) raramente atingem 50% num
 *   único dia. 30% captura o sinal de que é "dia útil normal" sem perder os casos
 *   onde só uma fração da empresa trabalha mas é genuinamente esperado.
 * - Fins de semana (sáb/dom) exigem evidência mais forte (60%): sábados com plantão
 *   minoritário (~20–30%) são comuns em varejo e saúde, mas não devem ser tratados
 *   como "dia de trabalho" para todos. O threshold elevado garante que só operações
 *   que genuinamente trabalham fim de semana em massa (ex.: varejo 7×1) sejam
 *   classificadas como workday — evita criar faltas artificiais pra quem não foi
 *   escalado.
 *
 * Histórico: thresholds antes eram 50% / 80%, mas em datasets reais com 300+
 * colaboradores e shifts rotativos rendiam 0 dias-de-trabalho detectados, levando
 * a "Total de faltas: 0" mesmo havendo faltas reais. 30% / 60% são equilibrados.
 */
const COMPANY_WORKDAY_THRESHOLD_WEEKDAY = 0.3;
const COMPANY_WORKDAY_THRESHOLD_WEEKEND = 0.6;

/** Retorna o threshold de presença adequado para a data (dia útil vs fim de semana). */
function companyWorkdayThreshold(date: string): number {
  const weekday = weekdayOf(date);
  // weekdayOf retorna 0 = domingo, 6 = sábado
  const isWeekend = weekday === 0 || weekday === 6;
  return isWeekend
    ? COMPANY_WORKDAY_THRESHOLD_WEEKEND
    : COMPANY_WORKDAY_THRESHOLD_WEEKDAY;
}

// =========================================================================
// Detecção de regime de plantão (12×36 e similares)
// =========================================================================

/**
 * Jornada média mínima (em minutos) para considerar um colaborador como
 * "plantonista de jornada longa". 10h cobre plantões 12×36 (12h),
 * 24×72 (24h), bombeiros (24h), e ainda exclui CLT padrão (8h–8h44).
 */
const LONG_SHIFT_MIN_AVG_MINUTES = 10 * 60;

/**
 * Fração mínima de sessões com workedMinutes ≥ LONG_SHIFT_MIN_AVG_MINUTES
 * que classifica o colaborador como plantonista de jornada longa, mesmo
 * quando a média fica abaixo do limite.
 *
 * Robustez a turnos splitados: plantões de 24h excedem
 * MAX_SESSION_SPAN_MINUTES (16h) em journeys.ts e são quebrados em 2
 * sessões — uma "metade longa" (~11h30) e uma "metade curta" (~30min)
 * com pares imbalanced. A média fica abaixo de 10h e o critério "média
 * ≥10h" deixa passar. Olhando a fração de sessões longas, plantão 24×72
 * tem 50% das sessões com workedMinutes ≥10h → 30% pega com folga, sem
 * confundir com colab CLT que ocasionalmente vira hora extra de 10h+.
 */
const LONG_SHIFT_MIN_LONG_SESSION_FRACTION = 0.3;

/**
 * Trabalho mínimo médio (min) para detectar plantão 12×36 DIURNO splitado
 * pelo SESSION_GAP_THRESHOLD em journeys.ts. Bug V4-A2: turno 07:00→07:00
 * com pausa de 30min no meio gera gap de ~17h entre 13:30 e 07:00 do dia
 * seguinte — o split da sessão produz uma fragmento balanced (~6h) + um
 * fragmento imbalanced (1 punch órfão). Critérios 1 (média ≥10h) e 2
 * (≥30% sessões ≥10h) falham porque NENHUMA sessão tem ≥10h. Plantonistas
 * legítimos viram "126 faltas fictícias / R$ 25.975 recuperáveis".
 *
 * Critério 3 (consistentPartialWork): TODAS as sessões do colab são
 * imbalanced AND média trabalhada ≥3h. Isso captura o split 12×36 diurno
 * sem confundir com:
 *  - CLT padrão: sessões balanced (4 punches) → critério não dispara
 *  - 12×36 noturno (V4-A5): sessões cabem dentro do gap → balanced
 *  - Pontos esquecidos esporádicos: nem todas sessões imbalanced
 */
const LONG_SHIFT_MIN_PARTIAL_SESSION_MINUTES = 3 * 60;

/**
 * Fração máxima de dias-de-trabalho-da-empresa em que o colaborador
 * efetivamente bateu ponto, dentro de sua janela [first..last], para
 * que ele seja classificado como plantonista de regime alternado.
 *
 * 12×36 puro: ~50% dos dias (15 de ~30).
 * 24×48: ~33% dos dias.
 * 24×72: ~25% dos dias.
 *
 * 0.65 cobre todos esses casos com folga, e não captura colaborador
 * comum que falta esporadicamente (que normalmente trabalha >85%).
 */
const ALTERNATING_SHIFT_MAX_PRESENCE_RATIO = 0.65;

/** Mínimo de sessões observadas pra inferir regime do colaborador. */
const ALTERNATING_SHIFT_MIN_SESSIONS = 4;

/**
 * Identifica colaboradores em regime de plantão alternado (12×36, 24×48,
 * 24×72, etc.) — combinam jornada típica longa (média ≥10h OU ≥30% das
 * sessões com ≥10h) com presença esparsa (≤65%). Para esses, dias sem
 * ponto dentro da janela são FOLGA PROGRAMADA da escala, não falta.
 * Excluí-los evita o anti-marketing de "525 faltas fictícias =
 * R$ 105.000 recuperáveis" em 12×36 e "114 faltas" em bombeiros 24×72
 * com plantão splitado pelo cap de span.
 */
function detectLongShiftAlternatingEmployees(
  journeys: DailyJourney[],
  companyWorkDays: ReadonlySet<string>,
): Set<string> {
  const result = new Set<string>();
  if (companyWorkDays.size === 0) return result;

  const byEmployee = new Map<string, DailyJourney[]>();
  for (const j of journeys) {
    if (j.workedMinutes <= 0) continue;
    let arr = byEmployee.get(j.employeeId);
    if (arr === undefined) {
      arr = [];
      byEmployee.set(j.employeeId, arr);
    }
    arr.push(j);
  }

  for (const [employeeId, empJourneys] of byEmployee) {
    if (empJourneys.length < ALTERNATING_SHIFT_MIN_SESSIONS) continue;

    const totalWorked = empJourneys.reduce((s, j) => s + j.workedMinutes, 0);
    const avgWorked = totalWorked / empJourneys.length;
    // Critério 1: média ≥10h (plantão 12×36 noturno puro, 24h sem split)
    // Critério 2: ≥30% sessões ≥10h (24×72 split em duas, metade longa)
    // Critério 3 (V4-A2): TODAS sessões imbalanced + média ≥3h (12×36
    //   diurno splitado pelo SESSION_GAP, ambas metades curtas).
    const longSessionCount = empJourneys.reduce(
      (s, j) => s + (j.workedMinutes >= LONG_SHIFT_MIN_AVG_MINUTES ? 1 : 0),
      0,
    );
    const longSessionFraction = longSessionCount / empJourneys.length;
    const allSessionsImbalanced = empJourneys.every((j) =>
      j.pairs.some((p) => p.imbalanced),
    );
    const consistentPartialWork =
      allSessionsImbalanced && avgWorked >= LONG_SHIFT_MIN_PARTIAL_SESSION_MINUTES;
    const isLongShift =
      avgWorked >= LONG_SHIFT_MIN_AVG_MINUTES ||
      longSessionFraction >= LONG_SHIFT_MIN_LONG_SESSION_FRACTION ||
      consistentPartialWork;
    if (!isLongShift) continue;

    let first = empJourneys[0]!.operationalDate;
    let last = empJourneys[0]!.operationalDate;
    const punchedSet = new Set<string>();
    for (const j of empJourneys) {
      punchedSet.add(j.operationalDate);
      if (j.operationalDate < first) first = j.operationalDate;
      if (j.operationalDate > last) last = j.operationalDate;
    }

    let companyDaysInWindow = 0;
    let punchedCompanyDays = 0;
    for (const cwd of companyWorkDays) {
      if (cwd < first || cwd > last) continue;
      companyDaysInWindow++;
      if (punchedSet.has(cwd)) punchedCompanyDays++;
    }

    if (companyDaysInWindow === 0) continue;

    const presenceRatio = punchedCompanyDays / companyDaysInWindow;
    if (presenceRatio <= ALTERNATING_SHIFT_MAX_PRESENCE_RATIO) {
      result.add(employeeId);
    }
  }

  return result;
}

/**
 * Public test seam: expõe o detector pra testes unitários sem precisar
 * exportar a função privada (mantém superfície API mínima).
 */
export function _internal_detectLongShiftEmployees(
  journeys: DailyJourney[],
  companyWorkDays: ReadonlySet<string>,
): Set<string> {
  return detectLongShiftAlternatingEmployees(journeys, companyWorkDays);
}

// =========================================================================
// Detecção de regime 6×1 com folgas rotativas (varejo, food service)
// =========================================================================

/**
 * Faixa de presença individual (% dos dias-de-trabalho-da-empresa dentro da
 * janela [first..last]) que caracteriza regime 6×1 com folgas rotativas.
 *
 * - Mínimo 75%: trabalha quase todo dia, exceto folga semanal (~1 em 7 ≈ 14%
 *   de ausência → 86% presença). Margem pra folgas eventuais e período
 *   parcial. Abaixo de 75%, presença é esparsa demais pra ser folga só
 *   semanal — provavelmente faltou de fato.
 * - Máximo 95%: cima dessa faixa o colaborador trabalhou tão consistentemente
 *   que folga rotativa não explica os poucos dias ausentes — é falta isolada
 *   real de quem normalmente trabalha 100%.
 */
const ROTATING_REST_MIN_PRESENCE_RATIO = 0.75;
const ROTATING_REST_MAX_PRESENCE_RATIO = 0.95;

/**
 * Mínimo de weekdays distintos em que o colaborador apareceu trabalhando.
 * Em 6×1 com folgas rotativas dentro de uma janela ≥7 dias, ele aparece
 * em pelo menos 5–6 weekdays diferentes (toda semana, exceto a folga).
 *
 * Cutoff em 4 separa folga rotativa de "falta crônica num weekday
 * específico" — ex: colab CLT que sempre falta na quarta-feira só
 * trabalha 4 weekdays distintos, mas com 1 deles fixo ausente. O critério
 * de 4 distintos exclui esse caso (≤3 ausentes de seg-sex), preservando
 * a inferência de falta crônica.
 */
const ROTATING_REST_MIN_WEEKDAYS = 4;

/**
 * Jornada média máxima — acima dela cai no detector de plantão alternado
 * (12×36, 24×48). Mantemos os dois detectores ortogonais: este captura
 * jornada normal (CLT 7h20–9h) com folga rotativa.
 */
const ROTATING_REST_MAX_AVG_MINUTES = 10 * 60;

/** Mínimo de sessões observadas pra inferir regime do colaborador. */
const ROTATING_REST_MIN_SESSIONS = 6;

/**
 * Mínimo de ausências dentro da janela [first..last] pra classificar como
 * regime 6×1 rotativo. Em escala 6×1 num mês cheio (~4 semanas), o colab
 * tem ~4 folgas semanais. Faltas isoladas reais raramente passam de 3 num
 * mesmo período sem virar leave-block ou padrão crônico.
 *
 * Cutoff em 4 protege o cenário N12 (faltas reais 2-3 dias por colab) de
 * ser confundido com regime rotativo — preserva a inferência de faltas
 * isoladas legítimas, que é a métrica de custo recuperável real.
 */
const ROTATING_REST_MIN_ABSENCES = 4;

/**
 * Máximo de ausências CONSECUTIVAS (na sequência de dias-úteis-da-empresa)
 * permitido para classificar como regime 6×1 rotativo.
 *
 * Bug V4-C2: atestado de 4 dias úteis seguidos (gripe, cirurgia, etc.)
 * batia TODOS os critérios de folga rotativa (presença 82%, ≥4 weekdays
 * distintos trabalhados, ≥4 ausências) — atestado era engolido pelo
 * detector e SUMIA das métricas. Anti-marketing oposto: empresa SABE
 * que o atestado existiu; sistema reportar 0 faltas nega realidade.
 *
 * Folgas rotativas legítimas: max_run = 1 (cada folga separada por
 * 4-5 dias úteis). Raramente 2 (Sex+Seg de semanas adjacentes).
 * Atestados, licenças e demissões: run ≥3.
 *
 * Cutoff em 2 separa: ≤2 = folga rotativa, ≥3 = bloco concentrado
 * (ainda processado como falta isolada se total <5 dias, ou leave-block).
 */
const ROTATING_REST_MAX_CONSECUTIVE_ABSENCES = 2;

/**
 * Identifica colaboradores em regime 6×1 com folgas rotativas — jornada
 * normal (<10h média) + presença individual entre 75% e 95% dos dias-de-
 * trabalho-da-empresa + ≥4 weekdays distintos trabalhados + ≥4 ausências
 * na janela (cadência semanal de folga, não falta esporádica).
 *
 * Para esses, dias sem ponto dentro da janela [first..last] são FOLGA
 * SEMANAL ROTATIVA da escala, não falta. Excluí-los evita o anti-marketing
 * de "125 faltas fictícias = R$ 25k recuperáveis" em varejo.
 *
 * Critério ortogonal ao detector de plantão alternado:
 *  - plantão: jornada longa (≥10h) + presença esparsa (≤65%)
 *  - 6×1: jornada normal (<10h) + presença alta (75–95%) + multi-weekday +
 *    cadência semanal (≥4 ausências)
 */
export function detectRotatingRestEmployees(
  journeys: DailyJourney[],
  companyWorkDays: ReadonlySet<string>,
): Set<string> {
  const result = new Set<string>();
  if (companyWorkDays.size === 0) return result;

  const byEmployee = new Map<string, DailyJourney[]>();
  for (const j of journeys) {
    if (j.workedMinutes <= 0) continue;
    let arr = byEmployee.get(j.employeeId);
    if (arr === undefined) {
      arr = [];
      byEmployee.set(j.employeeId, arr);
    }
    arr.push(j);
  }

  for (const [employeeId, empJourneys] of byEmployee) {
    if (empJourneys.length < ROTATING_REST_MIN_SESSIONS) continue;

    // Jornada média curta (CLT padrão). Acima de 10h cai no detector de plantão.
    const totalWorked = empJourneys.reduce((s, j) => s + j.workedMinutes, 0);
    const avgWorked = totalWorked / empJourneys.length;
    if (avgWorked >= ROTATING_REST_MAX_AVG_MINUTES) continue;

    // Janela do colaborador + dias com ponto + weekdays distintos
    let first = empJourneys[0]!.operationalDate;
    let last = empJourneys[0]!.operationalDate;
    const punchedSet = new Set<string>();
    const distinctWeekdays = new Set<number>();
    for (const j of empJourneys) {
      punchedSet.add(j.operationalDate);
      distinctWeekdays.add(weekdayOf(j.operationalDate));
      if (j.operationalDate < first) first = j.operationalDate;
      if (j.operationalDate > last) last = j.operationalDate;
    }

    if (distinctWeekdays.size < ROTATING_REST_MIN_WEEKDAYS) continue;

    let companyDaysInWindow = 0;
    let punchedCompanyDays = 0;
    for (const cwd of companyWorkDays) {
      if (cwd < first || cwd > last) continue;
      companyDaysInWindow++;
      if (punchedSet.has(cwd)) punchedCompanyDays++;
    }

    if (companyDaysInWindow === 0) continue;

    const absences = companyDaysInWindow - punchedCompanyDays;
    if (absences < ROTATING_REST_MIN_ABSENCES) continue;

    // Bug V4-C2: ausências CONSECUTIVAS na grade de dias-úteis-da-empresa
    // diferenciam folga rotativa (espalhada) de afastamento concentrado
    // (atestado de 3-4 dias seguidos). Sem esse check, atestado de 4 dias
    // que satisfaz os outros critérios (presença ~82%) era classificado
    // como 6×1 e SUMIA das métricas — anti-marketing oposto.
    const sortedWorkdays = [...companyWorkDays].sort();
    const workdayIndex = new Map<string, number>();
    sortedWorkdays.forEach((d, i) => workdayIndex.set(d, i));
    const absentDates: string[] = [];
    for (const cwd of sortedWorkdays) {
      if (cwd < first || cwd > last) continue;
      if (!punchedSet.has(cwd)) absentDates.push(cwd);
    }
    let maxRun = absentDates.length > 0 ? 1 : 0;
    let curRun = 1;
    for (let k = 1; k < absentDates.length; k++) {
      const prevIdx = workdayIndex.get(absentDates[k - 1]!)!;
      const curIdx = workdayIndex.get(absentDates[k]!)!;
      if (curIdx === prevIdx + 1) {
        curRun++;
        if (curRun > maxRun) maxRun = curRun;
      } else {
        curRun = 1;
      }
    }
    if (maxRun > ROTATING_REST_MAX_CONSECUTIVE_ABSENCES) continue;

    const presenceRatio = punchedCompanyDays / companyDaysInWindow;
    if (
      presenceRatio >= ROTATING_REST_MIN_PRESENCE_RATIO &&
      presenceRatio <= ROTATING_REST_MAX_PRESENCE_RATIO
    ) {
      result.add(employeeId);
    }
  }

  return result;
}

/**
 * Tupla (employeeId, date) detectada como falta inferida pela heurística.
 * Exposta pra que o drill-down possa listar os mesmos eventos contados pela
 * métrica agregada — uma única fonte de verdade evita divergência entre
 * "1722 inferidas" no tile e a lista exibida.
 *
 * `kind` separa faltas regulares (isolated, runs ≤4 dias) de blocos de
 * afastamento (leave-block, runs ≥5 dias úteis consecutivos = provável
 * atestado/licença/férias).
 */
export interface InferredAbsenceTuple {
  employeeId: string;
  date: string;
  /**
   * 'isolated' = falta regular (run ≤4 dias-de-trabalho consecutivos).
   * 'leave-block' = parte de um bloco de afastamento (run ≥5 dias).
   *
   * A separação é o que permite distinguir custo recuperável (faltas
   * isoladas) de benefício legal (atestado).
   */
  kind: 'isolated' | 'leave-block';
}

/**
 * Núcleo da heurística — retorna a lista de (employeeId, date) considerados
 * falta inferida. Reutilizado pela métrica agregada e pelo drill-down.
 *
 * Um dia é "dia de trabalho da empresa" quando >50% dos colaboradores ativos
 * bateram ponto. Para cada colaborador, conta apenas dias dentro da janela
 * [primeira marcação ... última marcação] — dias após o último ponto são
 * tratados como "provavelmente saiu da empresa" e não contam como falta.
 *
 * Retorna null quando não há colaboradores ativos OU nenhum dia de trabalho
 * detectado (sinaliza que a inferência não é aplicável).
 */
export function computeInferredAbsenceTuples(
  dataset: CanonicalDataset,
  journeys: DailyJourney[],
): InferredAbsenceTuple[] | null {
  const activeEmployeeIds = new Set(journeys.map(j => j.employeeId));
  if (activeEmployeeIds.size === 0) return null;

  const workersByDate = new Map<string, Set<string>>();
  for (const journey of journeys) {
    if (journey.workedMinutes > 0) {
      const workers = workersByDate.get(journey.operationalDate) ?? new Set<string>();
      workers.add(journey.employeeId);
      workersByDate.set(journey.operationalDate, workers);
    }
  }

  // Dias de trabalho da empresa = datas onde a presença supera o threshold
  // (threshold mais alto em sábados/domingos para evitar falsos positivos
  // em empresas com plantão minoritário — varejo, saúde, etc.).
  const companyWorkDays: string[] = [];
  const companyWorkDaysSet = new Set<string>();
  for (const [date, workers] of workersByDate) {
    const threshold = companyWorkdayThreshold(date);
    if (workers.size > activeEmployeeIds.size * threshold) {
      companyWorkDays.push(date);
      companyWorkDaysSet.add(date);
    }
  }
  if (companyWorkDays.length === 0) return null;

  // Plantonistas alternados (12×36, 24×48, etc.) E regime 6×1 com folgas
  // rotativas (varejo): folga é programada da escala, não falta. Excluí-los
  // evita inflar o "Custo das Faltas" (anti-marketing).
  const excluded = new Set<string>([
    ...detectLongShiftAlternatingEmployees(journeys, companyWorkDaysSet),
    ...detectRotatingRestEmployees(journeys, companyWorkDaysSet),
  ]);

  // Janela de presença por colaborador: [primeira marcação ... última marcação].
  // Dias fora dessa janela são tratados como "não fazia parte do quadro" e não
  // contam como falta.
  const punchedDatesByEmployee = new Map<string, Set<string>>();
  const firstPunchByEmployee = new Map<string, string>();
  const lastPunchByEmployee = new Map<string, string>();
  for (const journey of journeys) {
    if (journey.workedMinutes <= 0) continue;
    const dates = punchedDatesByEmployee.get(journey.employeeId) ?? new Set<string>();
    dates.add(journey.operationalDate);
    punchedDatesByEmployee.set(journey.employeeId, dates);

    const first = firstPunchByEmployee.get(journey.employeeId);
    if (first === undefined || journey.operationalDate < first) {
      firstPunchByEmployee.set(journey.employeeId, journey.operationalDate);
    }
    const last = lastPunchByEmployee.get(journey.employeeId);
    if (last === undefined || journey.operationalDate > last) {
      lastPunchByEmployee.set(journey.employeeId, journey.operationalDate);
    }
  }

  const aejAbsenceKeys = new Set<string>();
  for (const event of dataset.absenceBankEvents) {
    if (event.kind === 'absence') aejAbsenceKeys.add(`${event.employeeId}|${event.date}`);
  }

  // Lista ordenada de dias-de-trabalho-da-empresa: usada pra detectar
  // "consecutividade" de runs por colaborador (consecutivo = próximo workday
  // na lista, não próximo dia de calendário — assim sábados/domingos/feriados
  // que não são workdays da empresa não quebram o run).
  const sortedCompanyWorkDays = [...companyWorkDays].sort();

  // Coleta primeiro como tuplas sem `kind`, depois marca leave-block runs.
  const rawByEmployee = new Map<string, string[]>();
  for (const employeeId of activeEmployeeIds) {
    // Plantonistas alternados (12×36, 24×48) e regime 6×1 rotativo:
    // folga é programada, não falta.
    if (excluded.has(employeeId)) continue;
    const firstPunch = firstPunchByEmployee.get(employeeId);
    const lastPunch = lastPunchByEmployee.get(employeeId);
    if (firstPunch === undefined || lastPunch === undefined) continue;
    const punchedDates = punchedDatesByEmployee.get(employeeId) ?? new Set<string>();
    const absentDates: string[] = [];
    for (const workDay of sortedCompanyWorkDays) {
      if (workDay < firstPunch || workDay > lastPunch) continue;
      if (punchedDates.has(workDay)) continue;
      if (aejAbsenceKeys.has(`${employeeId}|${workDay}`)) continue;
      absentDates.push(workDay);
    }
    if (absentDates.length > 0) rawByEmployee.set(employeeId, absentDates);
  }

  // Detecção de runs: por colaborador, varremos sortedCompanyWorkDays e
  // marcamos sequências em que cada workday consecutivo está no set de
  // ausências. Runs ≥ LEAVE_BLOCK_MIN_DAYS viram 'leave-block'.
  const tuples: InferredAbsenceTuple[] = [];
  const workdayIndex = new Map<string, number>();
  sortedCompanyWorkDays.forEach((d, i) => workdayIndex.set(d, i));

  for (const [employeeId, absentDates] of rawByEmployee) {
    const absentSet = new Set(absentDates);
    // Varre absentDates em ordem cronológica, agrupando em runs onde
    // cada elemento ocupa o índice subsequente em sortedCompanyWorkDays
    // (consecutivo na grade de dias-de-trabalho).
    const sortedAbsent = [...absentDates].sort();
    let runStart = 0;
    while (runStart < sortedAbsent.length) {
      let runEnd = runStart;
      while (runEnd + 1 < sortedAbsent.length) {
        const cur = sortedAbsent[runEnd]!;
        const next = sortedAbsent[runEnd + 1]!;
        const curIdx = workdayIndex.get(cur);
        const nextIdx = workdayIndex.get(next);
        if (curIdx === undefined || nextIdx === undefined) break;
        if (nextIdx === curIdx + 1) {
          runEnd += 1;
        } else {
          break;
        }
      }
      const runLen = runEnd - runStart + 1;
      const kind: InferredAbsenceTuple['kind'] =
        runLen >= LEAVE_BLOCK_MIN_DAYS ? 'leave-block' : 'isolated';
      for (let i = runStart; i <= runEnd; i++) {
        tuples.push({ employeeId, date: sortedAbsent[i]!, kind });
      }
      runStart = runEnd + 1;
    }
    void absentSet;
  }
  return tuples;
}

/**
 * Agrupa as tuplas inferidas em "blocos de afastamento" — runs ≥
 * LEAVE_BLOCK_MIN_DAYS dias-de-trabalho consecutivos sem ponto, por
 * colaborador.
 *
 * Cada bloco contém startDate/endDate (extremos do run) e o número de dias
 * cobertos. Útil pra UI exibir "X colaboradores em afastamento longo no
 * período" e separar do custo recuperável.
 */
export function computeLeaveBlocks(tuples: InferredAbsenceTuple[]): LeaveBlock[] {
  const blocks: LeaveBlock[] = [];
  // Agrupa tuplas leave-block por employee. Já estão ordenadas dentro do run
  // pela ordem de inserção em computeInferredAbsenceTuples; reordenar aqui é
  // barato e isola este helper de invariantes externos.
  const byEmployee = new Map<string, string[]>();
  for (const t of tuples) {
    if (t.kind !== 'leave-block') continue;
    const arr = byEmployee.get(t.employeeId) ?? [];
    arr.push(t.date);
    byEmployee.set(t.employeeId, arr);
  }

  for (const [employeeId, datesUnsorted] of byEmployee) {
    const dates = [...datesUnsorted].sort();
    if (dates.length === 0) continue;
    // Como as tuplas vêm de runs já contíguos em workday-index, basta achar
    // os pontos onde o gap em dias de calendário ultrapassa um threshold
    // amplo. Aqui usamos a heurística mais simples: se a próxima data
    // existir e estiver "longe demais" (>14 dias calendário), é run novo.
    // Em geral cada employee só tem 1 leave-block por período, então essa
    // segmentação é raramente exercitada.
    let runStart = 0;
    for (let i = 1; i <= dates.length; i++) {
      const isBoundary =
        i === dates.length ||
        diffCalendarDays(dates[i - 1]!, dates[i]!) > 14;
      if (isBoundary) {
        const start = dates[runStart]!;
        const end = dates[i - 1]!;
        const days = i - runStart;
        blocks.push({ employeeId, startDate: start, endDate: end, days });
        runStart = i;
      }
    }
  }

  return blocks;
}

/** Diferença em dias de calendário entre duas datas YYYY-MM-DD. */
function diffCalendarDays(a: string, b: string): number {
  const aMs = new Date(a + 'T00:00:00Z').getTime();
  const bMs = new Date(b + 'T00:00:00Z').getTime();
  return Math.abs(Math.round((bMs - aMs) / (1000 * 60 * 60 * 24)));
}

/**
 * Resumo agregado dos blocos de afastamento detectados (count, totalDays,
 * affectedEmployees).
 */
export function summarizeLeaveBlocks(blocks: LeaveBlock[]): LeaveBlocksSummary {
  const employees = new Set<string>();
  let totalDays = 0;
  for (const b of blocks) {
    employees.add(b.employeeId);
    totalDays += b.days;
  }
  return {
    count: blocks.length,
    totalDays,
    affectedEmployees: employees.size,
  };
}

/**
 * Conjunto de datas consideradas "dia de trabalho da empresa" pela heurística.
 * Threshold de presença varia: >50% em dias úteis, >80% em sábados/domingos
 * (evita falsos positivos em empresas com plantão minoritário). Útil pra UI:
 * dias FORA desse conjunto são folga; dias DENTRO sem ponto e dentro da
 * janela [first..last] do colaborador são falta inferida.
 */
export function computeCompanyWorkDays(journeys: DailyJourney[]): Set<string> {
  const activeEmployeeIds = new Set(journeys.map(j => j.employeeId));
  const workersByDate = new Map<string, Set<string>>();
  for (const journey of journeys) {
    if (journey.workedMinutes > 0) {
      const workers = workersByDate.get(journey.operationalDate) ?? new Set<string>();
      workers.add(journey.employeeId);
      workersByDate.set(journey.operationalDate, workers);
    }
  }
  const days = new Set<string>();
  for (const [date, workers] of workersByDate) {
    const threshold = companyWorkdayThreshold(date);
    if (workers.size > activeEmployeeIds.size * threshold) days.add(date);
  }
  return days;
}

/**
 * Calcula faltas por heurística de "dia de trabalho da empresa".
 * Substitui as antigas calculateUnjustifiedAbsences (AEJ) e
 * calculateMissingExpectedDays (heurística simples). estimated=true.
 *
 * `value` segue contendo o TOTAL de tuplas (faltas isoladas + leave-blocks)
 * para preservar retrocompatibilidade. Use `calculateFaltasIsolated` ou
 * `calculateLeaveBlocksSummary` quando precisar separar.
 */
export function calculateFaltas(
  dataset: CanonicalDataset,
  journeys: DailyJourney[],
): OccurrenceMetric {
  const inferred = computeInferredAbsenceTuples(dataset, journeys);

  if (inferred === null) {
    return {
      kind: 'occurrence',
      code: 'absent.faltas',
      label: 'Faltas',
      helper: 'Sem colaboradores ativos ou nenhum dia de trabalho da empresa detectado (presença abaixo do threshold em todos os dias).',
      estimated: true,
      value: 0,
      affectedEmployees: 0,
    };
  }

  return {
    kind: 'occurrence',
    code: 'absent.faltas',
    label: 'Faltas',
    helper:
      'Dias-de-trabalho-da-empresa em que o colaborador não bateu ponto, dentro da janela ' +
      '[primeira ... última marcação] do colaborador (dias após o último ponto não contam — ' +
      'provavelmente saiu da empresa ou entrou em afastamento longo). Threshold de presença: ' +
      '>30% em dias úteis, >60% em sábados/domingos (exige evidência mais forte em fins de ' +
      'semana para evitar falsos positivos em empresas com plantão minoritário). Excluímos ' +
      'colaboradores em plantão alternado (12×36, 24×48 — jornada ≥10h e presença ≤65%) e ' +
      'em regime 6×1 com folgas rotativas (varejo — jornada <10h, presença individual entre ' +
      '75% e 95%, ≥4 weekdays distintos): pra esses, dias sem ponto são folga programada da ' +
      'escala, não falta. Estimado a partir do AFD. Inclui faltas isoladas (≤4 dias) e blocos ' +
      'de afastamento (≥5 dias) — ver `faltasIsolated` e `leaveBlocks` para a separação.',
    estimated: true,
    value: inferred.length,
    affectedEmployees: countUniqueEmployees(inferred.map(t => t.employeeId)),
  };
}

/**
 * Faltas inferidas excluindo blocos de afastamento (≥5 dias consecutivos).
 * Representa custo recuperável: comportamento típico de falta ad-hoc, não
 * atestado/licença.
 */
export function calculateFaltasIsolated(
  dataset: CanonicalDataset,
  journeys: DailyJourney[],
): OccurrenceMetric {
  const inferred = computeInferredAbsenceTuples(dataset, journeys);

  if (inferred === null) {
    return {
      kind: 'occurrence',
      code: 'absent.faltas_isolated',
      label: 'Faltas isoladas',
      helper: 'Sem colaboradores ativos ou nenhum dia de trabalho da empresa detectado.',
      estimated: true,
      value: 0,
      affectedEmployees: 0,
    };
  }

  const isolated = inferred.filter(t => t.kind === 'isolated');
  return {
    kind: 'occurrence',
    code: 'absent.faltas_isolated',
    label: 'Faltas isoladas',
    helper:
      'Subconjunto de `faltas` em que o colaborador ficou ausente por menos de ' +
      `${LEAVE_BLOCK_MIN_DAYS} dias-de-trabalho-da-empresa consecutivos. Representa o custo ` +
      'recuperável (faltas ad-hoc), separado de blocos de afastamento (atestado/licença/' +
      'férias) que entram em `leaveBlocks`.',
    estimated: true,
    value: isolated.length,
    affectedEmployees: countUniqueEmployees(isolated.map(t => t.employeeId)),
  };
}

/**
 * Resumo dos blocos de afastamento detectados — usado pra exibir "informativo,
 * não recuperável" na UI.
 */
export function calculateLeaveBlocksSummary(
  dataset: CanonicalDataset,
  journeys: DailyJourney[],
): LeaveBlocksSummary {
  const inferred = computeInferredAbsenceTuples(dataset, journeys);
  if (inferred === null) {
    return { count: 0, totalDays: 0, affectedEmployees: 0 };
  }
  const blocks = computeLeaveBlocks(inferred);
  return summarizeLeaveBlocks(blocks);
}

/**
 * Calcula o conjunto completo de métricas de ausência e absenteísmo.
 */
export function calculateAbsenceMetrics(
  dataset: CanonicalDataset,
  journeys: DailyJourney[],
  assumptions: DiagnosticAssumptions,
): AbsenceMetrics {
  const shortJourneys = calculateShortJourneys(journeys, assumptions.toleranceMinutes);
  const shortJourneyMinutes = calculateShortJourneyMinutes(journeys, assumptions.toleranceMinutes);
  const faltas = calculateFaltas(dataset, journeys);
  const faltasIsolated = calculateFaltasIsolated(dataset, journeys);
  const leaveBlocks = calculateLeaveBlocksSummary(dataset, journeys);

  const activeEmployees = countActiveEmployees(journeys);
  const periodDays = calcPeriodDays(dataset.period);

  return {
    shortJourneys,
    shortJourneyMinutes,
    faltas,
    faltasIsolated,
    leaveBlocks,
    shortJourneysPerEmployeeMonth: calcShortJourneysPerEmployeeMonth(
      shortJourneys.value, activeEmployees, periodDays,
    ),
    faltasPerEmployeeMonth: calcFaltasPerEmployeeMonth(
      faltas.value, activeEmployees, periodDays,
    ),
  };
}
